// Canonical helpers for building Apple TV route URLs. Replaces the four
// near-identical copies that previously lived in AppleTvClone.js,
// AppleTvListPage.js, AppleTvLibraryPage.js, AppleTvAccountPage.js,
// AppleTvSearch.js, AppleTvDetailPage.js, AppleTvCastCrewPage.js and
// AppleTvPersonPage.js.

import { appleHomePath, appleRoute } from "../appleTvHost";

/** Site name shown in the mobile top bar, sidebar header, and browser tab suffix. */
export const CURATOR_TV_SITE_NAME = "Curator TV";

export function slugifyTitle(value) {
  return String(value || "title")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Build the Apple TV show/movie route for a card item.
// `kindOverride` lets callers force "tv-show" or "movie" when item.mediaType
// isn't a reliable signal (e.g. detail page already knows the kind).
export function resolveTmdbId(item) {
  const raw = item?.tmdbId ?? item?.id;
  const direct = Number(raw);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const match = String(raw || "").match(/(?:movie|tv|person)-(\d+)/i);
  if (match) {
    const parsed = Number(match[1]);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
}

export function getApplePath(item, kindOverride) {
  if (!item) return appleHomePath();
  const explicit = kindOverride === "tv-show" || kindOverride === "movie"
    ? kindOverride
    : null;
  // item.mediaType can be "movie", "tv", or "tv-show" depending on source.
  const inferred = item.mediaType === "tv" || item.mediaType === "tv-show"
    ? "tv-show"
    : "movie";
  const kind = explicit || inferred;
  const title = item.title || item.name || "title";
  const id = resolveTmdbId(item);
  if (!id) return appleHomePath();
  return `${appleRoute(`/${kind}/${slugifyTitle(title)}/${id}`)}?ctx_agid=curator`;
}

// The one indexable URL for a title. Always the www root-domain path with no
// /apple prefix and no tracking query, so it matches the SSR canonical and the
// sitemap <loc> exactly (avoids "Discovered - currently not indexed").
export const CANONICAL_SITE_ORIGIN = "https://www.curatorwatchlist.com";

export function getCanonicalDetailUrl(item, kindOverride) {
  if (!item) return CANONICAL_SITE_ORIGIN;
  const explicit = kindOverride === "tv-show" || kindOverride === "movie"
    ? kindOverride
    : null;
  const inferred = item.mediaType === "tv" || item.mediaType === "tv-show"
    ? "tv-show"
    : "movie";
  const kind = explicit || inferred;
  const title = item.title || item.name || "title";
  const id = resolveTmdbId(item);
  if (!id) return CANONICAL_SITE_ORIGIN;
  return `${CANONICAL_SITE_ORIGIN}/${kind}/${slugifyTitle(title)}/${id}`;
}

export function getApplePersonPath(person) {
  const id = resolveTmdbId(person);
  if (!id) return appleHomePath();
  const slug = slugifyTitle(person?.name || person?.title || "person");
  return appleRoute(`/person/${slug}/${id}`);
}
